import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useTranslation } from "react-i18next";
import codService, { deriveCodReviewStatus } from "../../../services/CodService";
import { CodOrder } from "../../../types/codSalesRep";
import {
  CustomerOutstandingSummary,
  formatCodAmount,
  formatCodDate,
  getOrderOutstanding,
  isCodOrderOutstanding,
  nearlyEqualMoney,
} from "../../../utils/codUtils";
import { showToast } from "../../component/common/toastNotification";
import { CodCustomerOption, matchesCodCustomer } from "./codCustomerUtils";
import { glassCardSx, NJIM, codTableSx, codTableHeadCellSx } from "./codStyles";
import CodStatCard from "../../../components/ui/CodStatCard";
import StatusChip from "../../../components/ui/StatusChip";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import SearchField from "../../component/common/searchField";

interface CodOutstandingTabProps {
  refreshKey?: number;
  customer?: CodCustomerOption | null;
}

const CodOutstandingTab: React.FC<CodOutstandingTabProps> = ({
  refreshKey = 0,
  customer = null,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [endpointTotal, setEndpointTotal] = useState<number | null>(null);
  const [fromFallback, setFromFallback] = useState(false);
  // All-customers overview (shown when no customer is picked)
  const [summaries, setSummaries] = useState<CustomerOutstandingSummary[]>([]);
  const [overviewOrders, setOverviewOrders] = useState<CodOrder[]>([]);
  const [overviewSearch, setOverviewSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState("");

  const loadData = useCallback(async () => {
    if (!customer) {
      setOrders([]);
      setEndpointTotal(null);
      setLoading(true);
      try {
        const [rows, outstanding] = await Promise.all([
          codService.listOrders(),
          codService.getOutstandingByCustomer(),
        ]);
        setOverviewOrders(rows.filter(isCodOrderOutstanding));
        setSummaries(
          outstanding.items
            .filter((row) => row.outstandingAmount > 0)
            .sort((a, b) => b.outstandingAmount - a.outstandingAmount)
        );
        setFromFallback(outstanding.fromFallback);
      } catch {
        showToast(t("codSalesRep.loadFailed"), "error");
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const [allOrders, outstanding] = await Promise.all([
        codService.listOrders(),
        codService.getOutstandingByCustomer({
          customerId: customer.customerId || undefined,
          customerCode: customer.customerCode || undefined,
        }),
      ]);
      setOrders(
        allOrders.filter(
          (order) =>
            matchesCodCustomer(order, customer) && isCodOrderOutstanding(order)
        )
      );
      const row = outstanding.items.find(
        (item) =>
          (customer.customerId && item.customerId === customer.customerId) ||
          (customer.customerCode && item.customerCode === customer.customerCode)
      );
      setEndpointTotal(row?.outstandingAmount ?? outstanding.totalOutstandingAmount);
      setFromFallback(outstanding.fromFallback);
    } catch {
      showToast(t("codSalesRep.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [customer, t]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const visibleTotal = useMemo(
    () => orders.reduce((sum, order) => sum + getOrderOutstanding(order), 0),
    [orders]
  );

  const totalsDisagree =
    endpointTotal != null && !nearlyEqualMoney(endpointTotal, visibleTotal);

  if (loading) {
    return <TableSkeleton rows={5} columns={5} />;
  }

  if (!customer) {
    const keyword = overviewSearch.trim().toLowerCase();
    const visibleSummaries = keyword
      ? summaries.filter(
          (row) =>
            row.customerName.toLowerCase().includes(keyword) ||
            row.customerCode.toLowerCase().includes(keyword)
        )
      : summaries;
    const totalOutstanding = summaries.reduce((sum, row) => sum + row.outstandingAmount, 0);
    const totalUnpaid = summaries.reduce((sum, row) => sum + row.unpaidOrderCount, 0);

    return (
      <Box>
        {fromFallback && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            {t("codSalesRep.outstandingFallback")}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "minmax(0, 1.5fr) repeat(2, minmax(0, 1fr))" },
            gap: 2,
            mb: 3,
            "& > :first-of-type": { gridColumn: { xs: "1 / -1", lg: "auto" } },
          }}
        >
          <CodStatCard
            label={t("codSalesRep.outstandingValue")}
            value={formatCodAmount(totalOutstanding)}
            tone="danger"
            icon={<AccountBalanceWalletRoundedIcon />}
            compactValue
          />
          <CodStatCard
            label={t("codSalesRep.customersWithBalance")}
            value={summaries.length}
            tone="ink"
            icon={<GroupsRoundedIcon />}
          />
          <CodStatCard
            label={t("codSalesRep.unpaidOrders")}
            value={totalUnpaid}
            tone="warning"
            icon={<ReceiptLongRoundedIcon />}
          />
        </Box>

        <CodSectionHeader
          title={t("codSalesRep.outstandingByCustomer")}
          helper={t("codSalesRep.outstandingOverviewHint")}
        />

        {summaries.length === 0 ? (
          <EmptyState
            title={t("codSalesRep.emptyOutstandingTitle")}
            description={t("codSalesRep.noOutstandingCustomers")}
          />
        ) : (
          <>
            <SearchField
              value={overviewSearch}
              onChange={setOverviewSearch}
              placeholder={t("codSalesRep.searchCustomers")}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {visibleSummaries.length === 0 && (
                <EmptyState title={t("codSalesRep.noSearchMatches")} />
              )}
              {visibleSummaries.map((row) => {
                const key = row.customerId || row.customerCode;
                const isOpen = expandedKey === key;
                const option: CodCustomerOption = {
                  key,
                  customerId: row.customerId,
                  customerCode: row.customerCode,
                  customerName: row.customerName,
                };
                const customerOrders = isOpen
                  ? overviewOrders.filter((order) => matchesCodCustomer(order, option))
                  : [];
                const share = totalOutstanding > 0 ? (row.outstandingAmount / totalOutstanding) * 100 : 0;

                return (
                  <Box
                    key={key}
                    sx={{
                      borderRadius: "14px",
                      border: `1px solid ${isOpen ? NJIM.mint : NJIM.border}`,
                      background: NJIM.white,
                      boxShadow: isOpen ? NJIM.elevationHover : "none",
                      overflow: "hidden",
                      transition: `border-color 150ms ${NJIM.easeOut}, box-shadow 150ms ${NJIM.easeOut}`,
                    }}
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => setExpandedKey(isOpen ? "" : key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setExpandedKey(isOpen ? "" : key);
                        }
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr) auto",
                          md: "minmax(0, 1.6fr) minmax(0, 1fr) auto auto",
                        },
                        alignItems: "center",
                        gap: { xs: 1.5, md: 2.5 },
                        p: { xs: 1.5, md: 2 },
                        cursor: "pointer",
                        "&:hover": { background: NJIM.muted },
                        "&:focus-visible": { outline: `2px solid ${NJIM.mint}`, outlineOffset: -2 },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <CodInitialsAvatar name={row.customerName} size={40} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 14,
                              color: NJIM.textPrimary,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={row.customerName}
                          >
                            {row.customerName || row.customerCode}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                            {row.customerCode} · {t("codSalesRep.unpaidOrders")}: {row.unpaidOrderCount}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                        <Box sx={{ height: 6, borderRadius: 999, bgcolor: NJIM.muted, overflow: "hidden" }}>
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.max(share, 2)}%`,
                              borderRadius: 999,
                              bgcolor: NJIM.danger,
                              opacity: 0.75,
                            }}
                          />
                        </Box>
                        <Typography sx={{ mt: 0.5, fontSize: 11, color: NJIM.textMuted, fontVariantNumeric: "tabular-nums" }}>
                          {share.toFixed(1)}%
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: 14, md: 16 },
                          color: NJIM.danger,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                          textAlign: "end",
                        }}
                      >
                        {formatCodAmount(row.outstandingAmount, row.currency)}
                      </Typography>

                      <IconButton
                        size="small"
                        tabIndex={-1}
                        aria-hidden
                        sx={{
                          display: { xs: "none", md: "inline-flex" },
                          transform: isOpen ? "rotate(180deg)" : "none",
                          transition: `transform 180ms ${NJIM.easeOut}`,
                        }}
                      >
                        <KeyboardArrowDownRoundedIcon />
                      </IconButton>
                    </Box>

                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 2, pt: 0.5, borderTop: `1px solid ${NJIM.border}` }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
                          {customerOrders.map((order) => {
                            const status = deriveCodReviewStatus(order);
                            const sageRef = order.salesOrderNumber || order.sageOrderNumbers?.[0];
                            return (
                              <Box
                                key={order.id}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "minmax(0, 1fr) auto",
                                    sm: "minmax(0, 1.3fr) auto minmax(0, 1fr) auto",
                                  },
                                  alignItems: "center",
                                  gap: { xs: 1, sm: 2 },
                                  px: 1.5,
                                  py: 1.25,
                                  borderRadius: "12px",
                                  bgcolor: NJIM.muted,
                                  border: `1px solid ${NJIM.border}`,
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {order.number}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
                                    {formatCodDate(order.date)}
                                    {sageRef ? ` · ${t("codSalesRep.sageRef")} ${sageRef}` : ""}
                                  </Typography>
                                </Box>
                                <Box sx={{ justifySelf: { xs: "end", sm: "start" } }}>
                                  <StatusChip status={status} label={t(`codSalesRep.${status}`)} />
                                </Box>
                                <Box sx={{ minWidth: 0, textAlign: { xs: "start", sm: "end" } }}>
                                  <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>
                                    {t("common.total")}
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: NJIM.textPrimary, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {formatCodAmount(order.amount, order.currency)}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "end" }}>
                                  <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>
                                    {t("codSalesRep.remaining")}
                                  </Typography>
                                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: NJIM.danger, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {formatCodAmount(getOrderOutstanding(order), order.currency)}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {customerOrders.length === 0 && (
                          <Typography sx={{ mt: 1.5, fontSize: 13, color: NJIM.textSecondary }}>
                            {t("codSalesRep.noOutstandingCustomers")}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {fromFallback && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {t("codSalesRep.outstandingFallback")}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: totalsDisagree ? "repeat(3, 1fr)" : "repeat(2, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <CodStatCard
          label={t("codSalesRep.outstandingValue")}
          value={formatCodAmount(visibleTotal)}
          tone="danger"
          icon={<AccountBalanceWalletRoundedIcon />}
          compactValue
        />
        <CodStatCard
          label={t("codSalesRep.unpaidOrders")}
          value={orders.length}
          tone="warning"
          icon={<ReceiptLongRoundedIcon />}
        />
        {totalsDisagree && endpointTotal != null && (
          <CodStatCard
            label={t("codSalesRep.customerEndpointTotal")}
            value={formatCodAmount(endpointTotal)}
            tone="info"
            icon={<CloudSyncRoundedIcon />}
            compactValue
          />
        )}
      </Box>

      <Box sx={{ ...glassCardSx, p: 2.5, borderRadius: "14px", mb: 0 }}>
        <CodSectionHeader
          title={t("codSalesRep.outstandingForCustomer")}
          helper={`${customer.customerName} (${customer.customerCode})`}
        />

        {orders.length === 0 ? (
          <EmptyState
            title={t("codSalesRep.emptyOutstandingTitle")}
            description={t("codSalesRep.emptyOutstandingDesc")}
          />
        ) : (
          <Table size="small" sx={codTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.order")}</TableCell>
                <TableCell sx={codTableHeadCellSx}>{t("common.status")}</TableCell>
                <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.sageRef")}</TableCell>
                <TableCell align="right" sx={codTableHeadCellSx}>
                  {t("common.total")}
                </TableCell>
                <TableCell align="right" sx={codTableHeadCellSx}>
                  {t("codSalesRep.remaining")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {order.number}
                    </Typography>
                    {formatCodDate(order.date)}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      status={deriveCodReviewStatus(order)}
                      label={t(`codSalesRep.${deriveCodReviewStatus(order)}`)}
                    />
                  </TableCell>
                  <TableCell>{order.salesOrderNumber || order.sageOrderNumbers?.[0] || "—"}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatCodAmount(order.amount, order.currency)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: NJIM.danger, fontVariantNumeric: "tabular-nums" }}>
                    {formatCodAmount(getOrderOutstanding(order), order.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

export default CodOutstandingTab;
